import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, asset, IMG } from "@/lib/api";
import { PageHeader } from "@/components/shared/SectionHeader";
import { Calendar, ArrowRight, Star, Users, MessageSquare, Megaphone, Heart } from "lucide-react";

export default function Community() {
  const [events, setEvents] = useState([]);
  const [news, setNews] = useState([]);
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    api.get("/events").then(r => setEvents(r.data));
    api.get("/news").then(r => setNews(r.data));
    api.get("/testimonials").then(r => setTestimonials(r.data));
  }, []);

  return (
    <div>
      <PageHeader
        eyebrow="Community"
        title="A village of educators, learners and mentors."
        description="Monthly live Q&As, member spotlights, and success stories from members across 51 jurisdictions."
        image={IMG.community}
      />

      <section className="container-x py-16 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-8 space-y-16">
          <div>
            <div className="flex items-center justify-between">
              <div className="eyebrow">Upcoming Events</div>
              <Link to="/events" className="text-sm font-semibold hover:text-primary inline-flex items-center gap-2">View calendar <ArrowRight className="h-4 w-4" /></Link>
            </div>
            <div className="mt-4 divide-y divide-border rounded-lg border border-border">
              {events.slice(0, 4).map(e => {
                const d = new Date(e.date);
                return (
                  <div key={e.id} className="p-5 flex items-center gap-6" data-testid={`community-event-${e.id}`}>
                    <div className="text-center min-w-[64px]">
                      <div className="font-display text-3xl leading-none">{d.getDate()}</div>
                      <div className="eyebrow text-[10px] mt-1">{d.toLocaleString('en', { month: 'short' })}</div>
                    </div>
                    <div className="flex-1">
                      <div className="font-semibold">{e.title}</div>
                      <div className="text-xs text-muted-foreground mt-1">{e.kind} · {e.time} · {e.host}</div>
                    </div>
                    <Link to="/events" className="btn-outline !py-2 !px-4 text-xs">RSVP</Link>
                  </div>
                );
              })}
            </div>
          </div>

          <div>
            <div className="eyebrow">Featured Discussions</div>
            <div className="mt-4 grid md:grid-cols-2 gap-4">
              {[
                { t: "What are your first-90-days rituals?", a: "Priya S.", r: 42, icon: MessageSquare },
                { t: "Best containment fencing brands?", a: "James O.", r: 28, icon: MessageSquare },
                { t: "Raw feeding — where did you start?", a: "Dr. Mara Lee", r: 61, icon: MessageSquare },
                { t: "How do you handle guests?", a: "Kenji T.", r: 33, icon: MessageSquare },
              ].map((d, i) => {
                const Icon = d.icon;
                return (
                  <div key={i} className="rounded-lg border border-border bg-card p-5 hover:-translate-y-1 transition">
                    <div className="flex items-start gap-3">
                      <Icon className="h-4 w-4 mt-1 text-muted-foreground" />
                      <div>
                        <div className="font-semibold">{d.t}</div>
                        <div className="text-xs text-muted-foreground mt-1">by {d.a} · {d.r} replies</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div>
            <div className="eyebrow">Success Stories</div>
            <div className="mt-4 grid gap-4">
              {testimonials.map(t => (
                <figure key={t.id} className="rounded-lg border border-border bg-card p-6">
                  <blockquote className="font-display text-2xl italic leading-snug text-pretty">"{t.quote}"</blockquote>
                  <figcaption className="mt-3 eyebrow">— {t.author}, {t.role}</figcaption>
                </figure>
              ))}
            </div>
          </div>
        </div>

        <aside className="lg:col-span-4 space-y-6">
          <div className="rounded-lg border border-border bg-card p-6">
            <div className="flex items-center gap-2"><Star className="h-4 w-4 text-accent" /><div className="eyebrow">Member Spotlight</div></div>
            <div className="mt-4 font-display text-2xl">Priya & Sable</div>
            <div className="text-xs text-muted-foreground mt-1">Ownership 201 · PNW Chapter</div>
            <p className="mt-4 text-sm text-muted-foreground">"Two years ago I didn't even know what a content percentage was. Now I mentor future owners."</p>
          </div>
          <div className="rounded-lg border border-border bg-card p-6">
            <div className="flex items-center gap-2"><Users className="h-4 w-4 text-primary" /><div className="eyebrow">Breeder Spotlight</div></div>
            <div className="mt-4 font-display text-2xl">Whitepine Wolfdogs</div>
            <div className="text-xs text-muted-foreground mt-1">Missoula, MT</div>
            <p className="mt-4 text-sm text-muted-foreground">Small-scale, ethical program mentoring five certified placements this year.</p>
          </div>
          <div className="rounded-lg border border-border bg-card p-6">
            <div className="flex items-center gap-2"><Megaphone className="h-4 w-4 text-accent" /><div className="eyebrow">Announcements</div></div>
            <ul className="mt-4 space-y-3 text-sm">
              <li>2026 Annual Conference — early bird open</li>
              <li>New Breeder Program cohort launching in Q2</li>
              <li>Legal update: 4 states in active legislation</li>
            </ul>
          </div>
          <div className="rounded-lg border border-border bg-card p-6">
            <div className="flex items-center gap-2"><Heart className="h-4 w-4 text-accent" /><div className="eyebrow">Volunteer</div></div>
            <p className="mt-4 text-sm text-muted-foreground">Mentor a future owner. Support your local chapter. Help a sanctuary.</p>
            <Link to="/contact" className="btn-outline !py-2 !px-4 text-xs mt-4">Get involved</Link>
          </div>
        </aside>
      </section>
    </div>
  );
}
